import { useEffect, useState } from 'react';
import { Formik, Form } from 'formik';
import { isEqual } from 'lodash';
import Yup from '../../utils/yup';

import addNotification from '../../utils/addNotification';
import { NotificationType } from '../../config';
import useMenus from '../../context/MenuContext';
import useUsers from '../../context/UsersContext';
import { FormButtonPanelUI, FormHeaderUI, FormBase } from '../Interface';
import { cleanValues, generateAutocompleteItems } from '../../utils/helper';
import masterDataService from '../../service/masterData.service';
import itemCompanyCatalogService from '../../service/itemCompanyCatalog.service';
import ItemCompanyCatalogFields from './ItemCompanyCatalogFields';
import ItemCompanyCatalogReports from './ItemCompanyCatalogReports';

const ItemCompanyCatalogPage = () => {
  const { menus, getMenus, getMenuSubMenuId, getCanEdit } = useMenus();
  const { getUser, user } = useUsers();
  const [refetchTable, setRefetchTable] = useState(false);
  const [viewField, setViewField] = useState(true);
  const [editButtonVisible, setEditButtonvisible] = useState(true);
  const [addField, setAddField] = useState(true);
  const [canEdit, setCanEdit] = useState(true);
  const [autocompleteOptions, setAutocompleteOptions] = useState({
    company: [],
  });

  const [defaultValues, setDefaultValues] = useState({});
  const [selectedValues, setSelectedValues] = useState({});
  const [operatorsDisabled, setOperatorsDisabled] = useState(false);
  const [initValues, setInitValues] = useState({});
  const [defaultSelectedValues, setDefaultSelectedValues] = useState({});
  const [rowSelectionModel, setRowSelectionModel] = useState([]);

  const def = {
    itemCode: null,
    companyId: null,
    itemCodeInBp: '',
    itemNameInBp: '',
    active: true,
    note: '',
  };

  useEffect(() => {
    getMenus();
    if (!Object.keys(user).length) getUser();

    setDefaultValues(def);
    setDefaultSelectedValues(def);
    initDefaultValues();
  }, []);

  useEffect(() => {
    const submenuId = getMenuSubMenuId('itemCompanyCatalog');
    setCanEdit(getCanEdit(submenuId));
  }, [menus]);

  useEffect(() => {
    if (Object.keys(selectedValues).length > 0) {
      if (!isEqual(defaultSelectedValues, selectedValues)) {
        valuesToForm();
        setOperatorsDisabled(false);
      } else {
        setOperatorsDisabled(true);
        setInitValues(defaultValues);
      }
    } else {
      setOperatorsDisabled(true);
      setInitValues(defaultValues);
    }
  }, [selectedValues]);

  const initDefaultValues = () => {
    masterDataService.companyToForm().then((data) => {
      setAutocompleteOptions({
        company: generateAutocompleteItems(data, ['code', 'name'], 'id'),
      });
    });
  };

  const validationSchema = Yup.object().shape({
    itemCode: Yup.string().required().max(100),
    companyId: Yup.number().required(),
    itemCodeInBp: Yup.string().required().max(100),
    itemNameInBp: Yup.string().nullable().max(200),
    active: Yup.boolean(),
    note: Yup.string().nullable().max(3000),
  });

  const valuesToForm = () => {
    const { id, itemCode, companyId, itemCodeInBp, itemNameInBp, active, note } = selectedValues;

    setInitValues({
      id,
      itemCode,
      companyId,
      itemCodeInBp: itemCodeInBp ?? '',
      itemNameInBp: itemNameInBp ?? '',
      active,
      note: note ?? '',
    });
  };

  const handleSubmit = (values, { resetForm }) => {
    if (!editButtonVisible) {
      updateObject(values);
    } else if (!addField) {
      handleInsertObject(values, resetForm);
    }
  };

  const updateObject = async (values) => {
    const updData = {
      ...cleanValues(values),
    };

    itemCompanyCatalogService.updateItem(updData).then((data) => {
      addNotification({
        content: 'Sikeres módosítás!',
        type: NotificationType.SUCCESS,
      });

      setSelectedValues(data);
      setRefetchTable(true);
      setEditButtonvisible(true);
      setViewField(true);
    });
  };

  const handleInsertObject = async (values) => {
    const insertObject = {
      ...cleanValues(values),
    };

    itemCompanyCatalogService
      .createItem(insertObject)
      .then((data) => {
        addNotification({
          content: 'Sikeres mentés!',
          type: NotificationType.SUCCESS,
        });

        setSelectedValues(data);
        setRowSelectionModel(data.id ?? `${data.itemCode ?? ''}_${data.companyId ?? ''}`);
        setRefetchTable(true);
        setAddField(true);
        setViewField(true);
      })
      .finally(() => {
        //@TODO loader
      });
  };

  return (
    <FormBase
      name="ItemCompanyCatalog"
      leftPanelContent={
        <div className="h-full overflow-auto custom-scrollbar">
          <FormHeaderUI
            addClick={() => {
              setSelectedValues(defaultSelectedValues);
              setViewField(false);
              setAddField(false);
            }}
            addDisabled={!viewField || !canEdit}
            modClick={() => {
              setViewField(false);
              setEditButtonvisible(false);
            }}
            modDisabled={!viewField || operatorsDisabled || !canEdit}
            copyClick={() => {
              setSelectedValues({
                ...selectedValues,
                id: null,
              });
              setViewField(false);
              setAddField(false);
            }}
            copyDisabled={!viewField || operatorsDisabled || !canEdit}
          />

          <Formik
            initialValues={initValues}
            validationSchema={validationSchema}
            validateOnChange={false}
            onSubmit={handleSubmit}
            enableReinitialize
          >
            {({ resetForm }) => (
              <Form>
                <ItemCompanyCatalogFields
                  viewField={viewField}
                  autocompleteOptions={autocompleteOptions}
                  selectedValues={selectedValues}
                />

                <FormButtonPanelUI
                  onCancelClick={() => {
                    setViewField(true);
                    setEditButtonvisible(true);
                    setAddField(true);

                    resetForm();
                  }}
                  disabled={editButtonVisible && addField}
                />
              </Form>
            )}
          </Formik>
        </div>
      }
      rightPanelContent={
        <ItemCompanyCatalogReports
          refetch={{ refetchTable, setRefetchTable }}
          values={{ selectedValues, setSelectedValues }}
          viewField={viewField}
          operatorsDisabled={operatorsDisabled}
          rowSelection={{ rowSelectionModel, setRowSelectionModel }}
          autocompleteOptions={autocompleteOptions}
        />
      }
    />
  );
};

export default ItemCompanyCatalogPage;
